import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Business, Service, OperatingHours, BusinessBranding, ServiceReview } from '../types';
import { apiService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import UnifiedLayout from '../components/layouts/UnifiedLayout';
import SeoHead from '../components/SeoHead';
import { publicTenantSeo } from '../utils/publicSite';
import { filterCustomerBookableServices } from '../utils/serviceUtils';

interface BusinessWebsiteData {
  business: Business;
  services: Service[];
  operatingHours: OperatingHours[];
  branding?: BusinessBranding;
  serviceReviews: ServiceReview[];
}

const BusinessWebsitePage: React.FC = () => {
  const { businessShortName } = useParams<{ businessShortName: string }>();
  const [data, setData] = useState<BusinessWebsiteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<any[]>([]);

  useEffect(() => {
    const fetchBusinessData = async () => {
      if (!businessShortName) return;
      
      try {
        setLoading(true);
        // Customer-facing: exclude expired businesses.
        const businessData = await apiService.getBusiness(businessShortName, { includeExpired: false });
        
        // Fetch services WITH categories using the same approach as BusinessBookingPage
        // This ensures categories are properly included
        let servicesWithCategories: Service[] = [];
        try {
          servicesWithCategories = await apiService.getBusinessServicesByShortName(businessShortName);
        } catch (servicesError) {
          // Fallback to services from businessData if the separate call fails
          servicesWithCategories = businessData.services || [];
        }
        
        // Fetch categories separately (like BusinessBookingPage does)
        try {
          const categoriesData = await apiService.getServiceCategories(businessData.business.id);
          setCategories(categoriesData || []);
        } catch (categoriesError) {
          console.error('Error fetching categories:', categoriesError);
          setCategories([]);
        }
        
        // Fetch branding data
        let brandingData;
        try {
          brandingData = await apiService.getBusinessBranding(businessShortName);
          // Ensure logoUrl is properly handled - convert null to undefined and relative URLs to full URLs
          if (brandingData) {
            if (brandingData.logoUrl === null || brandingData.logoUrl === '') {
              brandingData.logoUrl = undefined;
            } else if (brandingData.logoUrl && typeof brandingData.logoUrl === 'string') {
              // Convert relative URL to full URL if needed
              if (!brandingData.logoUrl.startsWith('http://') && !brandingData.logoUrl.startsWith('https://')) {
                const apiBaseUrl = process.env.REACT_APP_API_URL || (process.env.REACT_APP_ENV === 'production' ? 'https://booking-i73o.onrender.com/api' : 'http://localhost:3001/api');
                brandingData.logoUrl = `${apiBaseUrl}${brandingData.logoUrl.startsWith('/') ? brandingData.logoUrl : `/${brandingData.logoUrl}`}`;
              }
            } else {
              brandingData.logoUrl = undefined;
            }
          }
        } catch (brandingError: any) {
          console.error('Error fetching branding data:', brandingError);
          // If branding fails, use default branding matching the unified layout
          brandingData = {
            logoUrl: undefined,
            primaryColor: '#e11d48',
            secondaryColor: '#9f1239',
            accentColor: '#f5c2cc',
            textColor: '#111827',
            backgroundColor: '#ffeef0',
            fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            useCustomColors: false,
            layout: 'unified' // Keep for backward compatibility but not used
          };
        }

        // Fetch public service reviews
        let serviceReviews: ServiceReview[] = [];
        try {
          serviceReviews = await apiService.getBusinessServiceReviews(businessData.business.id, 10);
        } catch (reviewsError) {
          // No service reviews or error fetching reviews
        }

        setData({
          ...businessData,
          services: filterCustomerBookableServices(servicesWithCategories),
          branding: brandingData,
          serviceReviews
        });
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to load business information');
      } finally {
        setLoading(false);
      }
    };

    fetchBusinessData();
  }, [businessShortName]);

  const formatTime = (time: string) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getDayName = (dayOfWeek: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  const tenantSeo = businessShortName
    ? publicTenantSeo({
        name: data?.business?.name || businessShortName,
        shortName: businessShortName,
        description: data?.business?.description,
      })
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        {tenantSeo ? (
          <SeoHead
            title="Business not found | AACore Booking"
            description="This business page is not available on AACore Booking."
            canonicalPath={tenantSeo.path}
            robots="noindex, follow"
          />
        ) : null}
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Business Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link 
            to="/login" 
            className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Login
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { business, services, operatingHours, branding, serviceReviews } = data;
  
  // Use default branding if not provided, and ensure logoUrl is properly handled
  const processedBranding: BusinessBranding = branding ? {
    ...branding,
    logoUrl: (branding.logoUrl && typeof branding.logoUrl === 'string' && branding.logoUrl.trim() !== '') 
      ? branding.logoUrl 
      : undefined
  } : {
    logoUrl: undefined,
    primaryColor: '#e11d48',
    secondaryColor: '#9f1239',
    accentColor: '#f5c2cc',
    textColor: '#111827',
    backgroundColor: '#ffeef0',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    useCustomColors: false,
    layout: 'modern'
  };
  
  // Render the unified layout
  return (
    <>
      {tenantSeo ? (
        <SeoHead
          title={tenantSeo.title}
          description={tenantSeo.description}
          canonicalPath={tenantSeo.path}
          jsonLd={tenantSeo.jsonLd}
        />
      ) : null}
      <UnifiedLayout
        business={business}
        services={services}
        operatingHours={operatingHours}
        branding={processedBranding}
        serviceReviews={serviceReviews}
        categories={categories}
        formatTime={formatTime}
        getDayName={getDayName}
        formatPrice={formatPrice}
        formatDuration={formatDuration}
      />
    </>
  );
};

export default BusinessWebsitePage; 